import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Eraser, Settings as SettingsIcon, X } from "lucide-react";
import {
  ReactTerminal,
  type ProviderConfig,
  type TerminalConfig,
  type TerminalController,
  type TerminalTheme,
} from "@aerelontech/react-terminal";
import "@aerelontech/react-terminal/styles.css";

import { AiTerminalSettings } from "@/components/AiTerminalSettings";
import { renderTerminalMessage } from "@/components/AiTerminalMessage";
import { Button } from "@/components/ui/button";
import { store } from "@/store";
import {
  addSlide,
  deleteSlide,
  moveSlide,
  setSlideLevel,
  setTitle,
  setTransition,
} from "@/store/documentSlice";
import { chartDocUpdated } from "@/store/chartSlice";
import {
  CHART_ROW_CAP,
  defaultEncoding,
  inferColumnType,
  newColumn,
  parseCell,
} from "@/lib/chart";
import type { ChartColumn, ChartRow } from "@/types/chart";
import type { SlideLevel, SlideTransition } from "@/types/document";
import {
  loadTerminalSettings,
  saveTerminalSettings,
  type TerminalProviderId,
  type TerminalSettings,
} from "@/lib/terminalSettings";

// The genAI assistant (packages/react-terminal), rendered as one of the editor's right-hand
// task panes rather than as a floating console.
//
// It used to be the package's default "floating" layout: a status bar pinned to the bottom of
// the window that opened upward over the app, which meant the studio had two competing panel
// systems — the tool panes on the right, and a full-width overlay at the bottom that the app
// had to shrink its own viewport around (the old `--ai-terminal-space` dance). Docking it
// makes the assistant a pane like the style sheet or the library: opened from the same icon
// rail, sized by the same resizable split, closed by the same X.
//
// `DeckEditor` owns the open/closed state (`panel === "assistant"`) and passes `onClose`; this
// component owns everything inside the pane — the header, the model picker, and the terminal
// itself. Providers/keys come from the user's runtime settings (`@/lib/terminalSettings`),
// edited via the gear in the header.

/**
 * The terminal's palette, expressed entirely in the studio's own design tokens (see the
 * `:root` block in `src/styles.css`) instead of a second hardcoded palette that would drift
 * from the app's. `theme.tokens` values are written straight through to `--rt-*` custom
 * properties on the terminal root, so `var(…)` references resolve against the app exactly as
 * they do for a Tailwind utility.
 */
const APP_THEME: TerminalTheme = {
  colorScheme: "light",
  tokens: {
    // Same white as the pane it sits in, so the terminal has no visible edge of its own.
    bg: "var(--card)",
    // Raised surfaces (assistant bubbles, the input, code spans) — the app's muted slate.
    surface: "var(--muted)",
    fg: "var(--foreground)",
    fgMuted: "var(--muted-foreground)",
    // Send button, caret, prompt sigil, links: the app's primary, exactly as a `default`
    // Button renders it.
    accent: "var(--primary)",
    accentFg: "var(--primary-foreground)",
    border: "var(--border)",
    danger: "var(--destructive)",
    // The pane is app chrome, not a console, so it takes the app's UI font. Monospace is
    // reapplied to code spans and fenced blocks only (`.ai-terminal-markdown` in styles.css).
    fontMono:
      'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    radius: "var(--radius-md)",
  },
};

/** Only surface providers that can actually run (a key, or keyless Ollama). */
function buildProviders(s: TerminalSettings): ProviderConfig[] {
  const providers: ProviderConfig[] = [];

  if (s.anthropic.apiKey.trim()) {
    providers.push({
      id: "anthropic",
      kind: "anthropic",
      label: "Claude",
      model: s.anthropic.model,
      apiKey: s.anthropic.apiKey.trim(),
      maxTokens: 1024,
    });
  }
  if (s.openai.apiKey.trim()) {
    providers.push({
      id: "openai",
      kind: "openai",
      label: "OpenAI",
      model: s.openai.model,
      apiKey: s.openai.apiKey.trim(),
      maxTokens: 1024,
    });
  }
  // Ollama needs no key — always available as a local fallback so the terminal is never inert.
  providers.push({
    id: "ollama",
    kind: "ollama",
    label: "Ollama (local)",
    model: s.ollama.model,
    baseUrl: s.ollama.baseUrl,
  });
  // Docker Model Runner (Qwen &c.): a keyless, OpenAI-compatible local endpoint, so it maps to
  // the `custom` kind speaking the OpenAI protocol. Only surfaced when a base URL is set.
  //
  // The token budget is deliberately far larger than the cloud providers' 1024. The default
  // local model (`ai/qwen3.6`) is a **reasoning** model: it spends completion tokens on
  // `reasoning_content` before it writes a single visible character — measured at ~1800 for a
  // one-sentence greeting. A 1024 cap does not truncate the answer, it prevents one: the
  // response finishes on `length` mid-thought with `content` empty, and the terminal shows
  // nothing at all. Raise this, never lower it, if the default model changes.
  if (s.qwen.baseUrl.trim()) {
    providers.push({
      id: "qwen",
      kind: "custom",
      protocol: "openai",
      label: "Qwen (local)",
      model: s.qwen.model,
      baseUrl: s.qwen.baseUrl.trim(),
      maxTokens: 8192,
    });
  }

  return providers;
}

// §18.1.1 A tool argument is whatever the model typed, so the level is narrowed here rather
// than cast: an unrecognised string becomes `undefined` and the reducer's own default (or, for
// setSlideLevel, "storyline") applies, instead of a junk value reaching `SlideData`.
function asLevel(value: unknown): SlideLevel | undefined {
  return value === "storyline" || value === "evidence" || value === "backup"
    ? value
    : undefined;
}

/**
 * Deck actions the assistant may call as tools (and the user via `/dispatch`). These are the
 * exact reducers DeckEditor dispatches, so an LLM edit is indistinguishable from a UI edit and
 * flows through the same persistence path.
 */
const REDUX_ACTIONS: NonNullable<TerminalConfig["redux"]>["actions"] = {
  addSlide: {
    create: (arg) =>
      addSlide({
        afterId: arg?.afterId ? String(arg.afterId) : undefined,
        level: asLevel(arg?.level),
      }),
    description:
      "Add a new, empty slide. Appends to the end of the deck unless `afterId` names " +
      "the slide to insert it directly below (§18.2.2). `level` places it in the pyramid " +
      "(§18.1.1) — omit it to inherit nothing and land on the storyline.",
    schema: {
      type: "object",
      properties: {
        afterId: {
          type: "string",
          description:
            "Id of the slide to insert below. Omit to append to the end of the deck.",
        },
        level: {
          type: "string",
          enum: ["storyline", "evidence", "backup"],
          description:
            "The new slide's level. `evidence` makes it support the nearest storyline " +
            "slide above it; `backup` puts it in the section behind the deck.",
        },
      },
      additionalProperties: false,
    },
  },
  // §18.2.11 "Add three evidence slides under the pricing slide" is the request the pyramid
  // invites, so the assistant gets the same two reducers the chrome row dispatches — no more.
  setSlideLevel: {
    create: (arg) =>
      setSlideLevel({
        id: String(arg?.id ?? ""),
        level: asLevel(arg?.level) ?? "storyline",
      }),
    description:
      "Set a slide's level in the pyramid: `storyline` (the argument), `evidence` (supports " +
      "the storyline slide above it) or `backup` (the section behind the deck). Refused on " +
      "the deck's first slide, which always carries the first claim (§18.2.1).",
    schema: {
      type: "object",
      properties: {
        id: { type: "string", description: "The slide's id." },
        level: {
          type: "string",
          enum: ["storyline", "evidence", "backup"],
        },
      },
      required: ["id", "level"],
      additionalProperties: false,
    },
  },
  setTitle: {
    create: (arg) => setTitle(String(arg?.title ?? "")),
    description: "Set the deck's title.",
    schema: {
      type: "object",
      properties: { title: { type: "string", description: "New deck title." } },
      required: ["title"],
      additionalProperties: false,
    },
  },
  moveSlide: {
    create: (arg) =>
      moveSlide({
        id: String(arg?.id ?? ""),
        direction: arg?.direction === "up" ? "up" : "down",
      }),
    description: "Move a slide up or down by its id.",
    schema: {
      type: "object",
      properties: {
        id: { type: "string", description: "The slide's id." },
        direction: { type: "string", enum: ["up", "down"] },
      },
      required: ["id", "direction"],
      additionalProperties: false,
    },
  },
  deleteSlide: {
    create: (arg) => deleteSlide({ id: String(arg?.id ?? "") }),
    description: "Archive (soft-delete) a slide by its id.",
    schema: {
      type: "object",
      properties: { id: { type: "string", description: "The slide's id." } },
      required: ["id"],
      additionalProperties: false,
    },
  },
  setTransition: {
    // Only forward keys the caller supplied — the reducer merges the patch, so an undefined
    // field would otherwise overwrite the existing setting.
    create: (arg) => {
      const patch: Partial<SlideTransition> = {};
      if (arg?.type !== undefined)
        patch.type = arg.type as SlideTransition["type"];
      if (arg?.durationMs !== undefined)
        patch.durationMs = Number(arg.durationMs);
      return setTransition(patch);
    },
    description:
      "Update the deck's slide-transition (e.g. its type or duration in ms).",
    schema: {
      type: "object",
      properties: {
        type: { type: "string", description: "Transition type." },
        durationMs: {
          type: "number",
          description: "Duration in milliseconds.",
        },
      },
      additionalProperties: false,
    },
  },
  // §15/§10.1 "Chart this table" — the one chart action worth exposing, and the reason the
  // other one is not.
  //
  // §10.1 suggests surfacing `chartDocCreated` alongside this. It is deliberately absent: a
  // `ReduxActionSpec` returns exactly ONE action (see the type), and a chart body created
  // without a slide window pointed at it in the same tick is an orphan — `ownerForContentDoc`
  // finds no slide holding it, the persistence listener files it under the deck, and nothing
  // ever renders or collects it. Creating a chart needs two dispatches, which this surface
  // cannot express; the user clicks "Add chart" and the assistant fills it, which is the whole
  // of what "chart this table" needs.
  setChartData: {
    create: (arg) => {
      const docId = String(arg?.chartDocId ?? "");
      const current = store.getState().chart[docId];
      const headers = Array.isArray(arg?.columns)
        ? (arg.columns as unknown[]).map((c) => String(c))
        : [];
      const grid = Array.isArray(arg?.rows) ? (arg.rows as unknown[]) : [];
      // A tool argument is whatever the model produced, so every field is narrowed rather
      // than cast — the same rule `asLevel` above follows. A malformed call must not be able
      // to put a shape into `chartSlice` that no renderer can read.
      const columns: ChartColumn[] = headers.map((label) => newColumn(label));
      const rows: ChartRow[] = grid
        .slice(0, CHART_ROW_CAP)
        .filter((line): line is unknown[] => Array.isArray(line))
        .map((line) => {
          const row: ChartRow = {};
          columns.forEach((column, i) => {
            const value = line[i];
            row[column.id] =
              typeof value === "number"
                ? value
                : value == null
                  ? null
                  : parseCell(String(value));
          });
          return row;
        });
      const typedColumns = columns.map((column) => ({
        ...column,
        type: inferColumnType(rows.map((r) => r[column.id])),
      }));
      // No such chart, or nothing usable in the call: dispatch a no-op the reducer ignores
      // rather than throwing across the tool boundary, where the failure would surface as a
      // dead assistant rather than as an answer.
      if (!current || typedColumns.length === 0) {
        return chartDocUpdated({ docId, doc: current });
      }
      return chartDocUpdated({
        docId,
        doc: {
          ...current,
          columns: typedColumns,
          rows,
          // Ids are all new, so the old encoding points at nothing. Re-derived rather than
          // repaired: the assistant just replaced the whole table.
          encoding: defaultEncoding(typedColumns),
          // Typed in by the assistant is a manual source, whatever the chart used to pull.
          source: { kind: "manual" },
        },
      });
    },
    description:
      "Replace a chart's data. `chartDocId` is the id on the slide's chart content window. " +
      "`columns` is the header row; `rows` is a list of rows, each a list of values in the " +
      "same order. The first non-numeric column becomes the category axis and every numeric " +
      "column is plotted. Chart type and styling are not changed.",
    schema: {
      type: "object",
      properties: {
        chartDocId: {
          type: "string",
          description: "The chart window's chartDocId.",
        },
        columns: {
          type: "array",
          items: { type: "string" },
          description: "Column headers, in order.",
        },
        rows: {
          type: "array",
          items: { type: "array" },
          description:
            "Rows, each a list of values in the same order as `columns`.",
        },
      },
      required: ["chartDocId", "columns", "rows"],
      additionalProperties: false,
    },
  },
};

/** The pane's title. Also the terminal's own `config.title`, so the two cannot drift. */
const PANE_TITLE = "AI Assistant";

export function AiTerminal({
  onClose,
  switcher,
}: {
  onClose?: () => void;
  // The other panels' icons, so the assistant's own header carries the same one-click switch
  // every other pane's does. Rendered rather than rebuilt here: this component knows nothing
  // about the panel set, and it should not have to.
  switcher?: ReactNode;
}) {
  const [settings, setSettings] =
    useState<TerminalSettings>(loadTerminalSettings);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // The controller is captured once via onReady. Only ever used for imperative, stable
  // callbacks (`clearMessages`); anything the header needs to RENDER reactively comes from
  // `settings` instead, which this component owns.
  const controllerRef = useRef<TerminalController | null>(null);

  const providers = useMemo(() => buildProviders(settings), [settings]);

  // The settings may name a provider that is not currently runnable (a cloud key was cleared),
  // in which case the package falls back to the first one. Mirror that here so the picker
  // shows what is actually answering rather than a stale selection.
  const activeProviderId = providers.some(
    (p) => p.id === settings.activeProviderId,
  )
    ? settings.activeProviderId
    : providers[0]?.id;

  const config = useMemo<TerminalConfig>(
    () => ({
      layout: "docked",
      title: PANE_TITLE,
      greeting:
        "Ask about your deck, or type /help. Set your API keys with the ⚙ button above. " +
        "I can add, move, and delete slides.",
      placeholder: "Ask the assistant, or type /help…",
      providers,
      activeProviderId,
      systemPrompt: settings.systemPrompt,
      // The pane is opened from the icon rail; a global key binding that toggles nothing
      // visible would only swallow ctrl+` from the rest of the app.
      hotkey: null,
      redux: {
        store,
        actions: REDUX_ACTIONS,
      },
      theme: APP_THEME,
    }),
    [providers, activeProviderId, settings.systemPrompt],
  );

  const handleSave = useCallback((next: TerminalSettings) => {
    saveTerminalSettings(next);
    setSettings(next);
    setSettingsOpen(false);
  }, []);

  const handleProviderChange = useCallback((id: TerminalProviderId) => {
    setSettings((prev) => {
      const next = { ...prev, activeProviderId: id };
      saveTerminalSettings(next);
      return next;
    });
  }, []);

  // `config.activeProviderId` only SEEDS the controller's initial selection, so a later change
  // has to be pushed in. As an effect rather than inside `handleProviderChange`, because the
  // settings dialog can change the default provider too and that path would otherwise leave
  // the picker and the model actually answering out of step. Safe no-op before onReady fires.
  useEffect(() => {
    if (activeProviderId) {
      controllerRef.current?.setActiveProvider(activeProviderId);
    }
  }, [activeProviderId]);

  return (
    // Fills the pane: header, model row, then the terminal taking whatever is left. The pane's
    // own `<aside>` draws the left border and the white ground.
    <div className="flex h-full min-h-0 flex-col">
      {/* Deliberately mirrors the other task panes' header (see `sidePanel` in DeckEditor) so
          the assistant reads as one of them, not as a guest — which now means ICONS ONLY, with
          no title: the switcher's blue Sparkles says this is the assistant, and dropping the
          words is what leaves room for the pane's own two controls at 320px. `PANE_TITLE` is
          still the terminal's `config.title`, so the name has not gone anywhere. */}
      <div className="flex items-center gap-1 border-b border-slate-200 px-2 py-2">
        {switcher}
        <div className="min-w-0 flex-1" />
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={() => setSettingsOpen(true)}
          aria-label={`${PANE_TITLE} settings`}
          title={`${PANE_TITLE} settings`}
        >
          <SettingsIcon className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          onClick={() => controllerRef.current?.clearMessages()}
          aria-label="Clear conversation"
          title="Clear conversation"
        >
          <Eraser className="h-4 w-4" />
        </Button>
        {onClose && (
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={onClose}
            aria-label="Close panel"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Which model answers, on its own row rather than crammed into the header: the pane can
          be dragged down to 320px, and a truncated model name is worse than no name. */}
      {providers.length > 0 && (
        <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-4 py-1.5">
          <span className="shrink-0 text-[11px] font-medium uppercase tracking-wide text-slate-500">
            Model
          </span>
          <select
            className="min-w-0 flex-1 truncate rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 outline-none focus-visible:ring-1 focus-visible:ring-ring"
            value={activeProviderId}
            onChange={(e) =>
              handleProviderChange(e.target.value as TerminalProviderId)
            }
            aria-label="Active model"
          >
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label ?? p.id} · {p.model}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="min-h-0 flex-1">
        <ReactTerminal
          className="ai-terminal-root"
          config={config}
          slots={{
            // The pane header above already carries the title and every control the packaged
            // one offers, so the terminal's own header would just be a second one.
            header: () => null,
            renderMessage: renderTerminalMessage,
          }}
          onReady={(controller) => {
            controllerRef.current = controller;
          }}
        />
      </div>

      <AiTerminalSettings
        open={settingsOpen}
        onOpenChange={setSettingsOpen}
        settings={settings}
        onSave={handleSave}
      />
    </div>
  );
}
